import React, { useState, useCallback, useEffect } from 'react';
import { Input, DatePicker, Spin, Row, Col, Card, Modal } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useModulePermission } from '../../hooks/usePermission';
import './D214Form.css';

import { useD214Mode } from './hooks/useD214Mode';
import { useD214Record } from './hooks/useD214Record';
import { useD214Navigation } from './hooks/useD214Navigation';
import ActionBar from './components/ActionBar';
import DetailGrid from './components/DetailGrid';
import type { FC170, FC171 } from '../../services/d214Api';

// ====================================================================
// Empty master template
// ====================================================================

const EMPTY_MASTER: Partial<FC170> = {
  fc170_01: '',
  fc170_02: 1,
  fc170_11: '',
  fc170_12: new Date().toISOString(),
  fc170_13: '',
  fc170_21: 0,
  fc170_91: '',
  fc170_92: null,
  fc170_93: '',
};

// ====================================================================
// D214Form
// ====================================================================

const D214Form: React.FC = () => {
  const navigate = useNavigate();
  const permissions = useModulePermission('pwd214f1');
  const { mode, toBrowse, toAdd, toEdit, isEditing } = useD214Mode();
  const { record, loading, saving, load, save, remove } = useD214Record();
  const { list, hasPrev, hasNext, fetchList, setCurrentByKey, goFirst, goLast, goPrev, goNext } = useD214Navigation();

  const [formData, setFormData] = useState<Partial<FC170>>({ ...EMPTY_MASTER });
  const [details, setDetails] = useState<Partial<FC171>[]>([]);
  const [selectedDetailIdx, setSelectedDetailIdx] = useState(-1);

  // Sync record → form
  useEffect(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
      setSelectedDetailIdx(-1);
    }
  }, [record]);

  // Load list on mount
  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // ----------------------------------------------------------------
  // Field helpers
  // ----------------------------------------------------------------

  const getField = useCallback(<K extends keyof FC170>(k: K) => formData[k] ?? '' as FC170[K], [formData]);

  const setField = useCallback(<K extends keyof FC170>(k: K, v: FC170[K]) => {
    setFormData(prev => ({ ...prev, [k]: v }));
  }, []);

  // ----------------------------------------------------------------
  // Navigation
  // ----------------------------------------------------------------

  const loadRecord = useCallback(async (scNo: string, itemNo: number) => {
    const result = await load(scNo, itemNo);
    if (result) setCurrentByKey(scNo, itemNo);
    return result;
  }, [load, setCurrentByKey]);

  const handleFirst = useCallback(async () => {
    const key = goFirst();
    if (key) await loadRecord(key.scNo, key.itemNo);
  }, [goFirst, loadRecord]);

  const handlePrev = useCallback(async () => {
    const key = goPrev();
    if (key) await loadRecord(key.scNo, key.itemNo);
  }, [goPrev, loadRecord]);

  const handleNext = useCallback(async () => {
    const key = goNext();
    if (key) await loadRecord(key.scNo, key.itemNo);
  }, [goNext, loadRecord]);

  const handleLast = useCallback(async () => {
    const key = goLast();
    if (key) await loadRecord(key.scNo, key.itemNo);
  }, [goLast, loadRecord]);

  // ----------------------------------------------------------------
  // Mode transitions
  // ----------------------------------------------------------------

  const handleAdd = useCallback(() => {
    setFormData({ ...EMPTY_MASTER, fc170_12: new Date().toISOString() });
    setDetails([]);
    setSelectedDetailIdx(-1);
    toAdd();
  }, [toAdd]);

  const handleEdit = useCallback(() => {
    toEdit();
  }, [toEdit]);

  const handleCancel = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ? record.details.map(d => ({ ...d })) : []);
    } else {
      setFormData({ ...EMPTY_MASTER });
      setDetails([]);
    }
    setSelectedDetailIdx(-1);
    toBrowse();
  }, [record, toBrowse]);

  // ----------------------------------------------------------------
  // Save
  // ----------------------------------------------------------------

  const handleSave = useCallback(async () => {
    const result = await save(formData, details, mode === 'add');
    if (result.success) {
      await fetchList();
      if (formData.fc170_01) setCurrentByKey(formData.fc170_01, formData.fc170_02 || 1);
      toBrowse();
    }
  }, [formData, details, mode, save, fetchList, setCurrentByKey, toBrowse]);

  // ----------------------------------------------------------------
  // Delete
  // ----------------------------------------------------------------

  const handleDelete = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: '請確認刪除本資料！',
      okText: '確認',
      cancelText: '取消',
      onOk: async () => {
        const ok = await remove(record.fc170_01, record.fc170_02);
        if (ok) {
          await fetchList();
          setFormData({ ...EMPTY_MASTER });
          setDetails([]);
          toBrowse();
        }
      },
    });
  }, [record, remove, fetchList, toBrowse]);

  // ----------------------------------------------------------------
  // Search
  // ----------------------------------------------------------------

  const handleSearch = useCallback(() => {
    const kw = formData.fc170_01?.trim();
    fetchList(kw);
  }, [formData.fc170_01, fetchList]);

  // ----------------------------------------------------------------
  // Detail operations
  // ----------------------------------------------------------------

  const handleAddDetail = useCallback(() => {
    const nextSeq = details.length + 1;
    if (nextSeq > 99) return;
    const newRow: Partial<FC171> = {
      fc171_01: formData.fc170_01 || '',
      fc171_02: formData.fc170_02 || 1,
      fc171_03: nextSeq,
      fc171_11: 0,
      fc171_112: '',
    };
    setDetails(prev => [...prev, newRow]);
    setSelectedDetailIdx(nextSeq - 1);
    setField('fc170_21', nextSeq);
  }, [details, formData.fc170_01, formData.fc170_02, setField]);

  const handleDeleteDetail = useCallback(() => {
    if (selectedDetailIdx < 0) return;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedDetailIdx);
      // Reindex seq nos
      return next.map((r, i) => ({ ...r, fc171_03: i + 1 }));
    });
    setSelectedDetailIdx(prev => Math.max(-1, prev - 1));
    setField('fc170_21', details.length - 1);
  }, [selectedDetailIdx, details.length, setField]);

  const handleDetailChange = useCallback((idx: number, field: keyof FC171, value: unknown) => {
    setDetails(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  }, []);

  // ----------------------------------------------------------------
  // Exit
  // ----------------------------------------------------------------
  const handleExit = useCallback(() => navigate(-1), [navigate]);

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------

  return (
    <div className="d214-form">
      <Spin spinning={loading}>
        {/* Header info */}
        <Card size="small" className="d214-header-card">
          <Row gutter={[16, 8]}>
            <Col span={4}>
              <label>S/C NO</label>
              <Input
                size="small"
                value={String(getField('fc170_01'))}
                onChange={e => setField('fc170_01', e.target.value)}
                disabled={!isEditing || mode === 'edit'}
                placeholder="S/C NO"
              />
            </Col>
            <Col span={3}>
              <label>S/C 項次</label>
              <Input
                size="small"
                type="number"
                value={Number(getField('fc170_02'))}
                onChange={e => setField('fc170_02', parseInt(e.target.value, 10) || 1)}
                disabled={!isEditing || mode === 'edit'}
              />
            </Col>
            <Col span={4}>
              <label>入單日期</label>
              <DatePicker
                size="small"
                value={formData.fc170_12 ? dayjs(formData.fc170_12) : null}
                onChange={d => setField('fc170_12', d ? d.toISOString() : null)}
                disabled={!isEditing || mode === 'edit'}
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
              />
            </Col>
            <Col span={4}>
              <label>入單人員</label>
              <Input
                size="small"
                value={String(getField('fc170_11'))}
                onChange={e => setField('fc170_11', e.target.value)}
                disabled={!isEditing}
                placeholder="人員代號"
              />
            </Col>
            <Col span={3}>
              <label>總項數</label>
              <Input size="small" value={Number(getField('fc170_21'))} disabled />
            </Col>
            <Col span={6}>
              <label>備註</label>
              <Input
                size="small"
                value={String(getField('fc170_13'))}
                onChange={e => setField('fc170_13', e.target.value)}
                disabled={!isEditing}
              />
            </Col>
          </Row>
        </Card>

        {/* Detail Grid */}
        <DetailGrid
          details={details}
          isEditing={isEditing}
          selectedIdx={selectedDetailIdx}
          onSelect={setSelectedDetailIdx}
          onAdd={handleAddDetail}
          onDelete={handleDeleteDetail}
          onChange={handleDetailChange}
        />

        {/* Process Completion Dates — BR-004 U_REFRESH */}
        {selectedDetailIdx >= 0 && details[selectedDetailIdx] && (
          <Card size="small" className="d214-completion-card" title="加工製程 / 實際完成日">
            <Row gutter={[12, 8]}>
              {([
                { field: 'fc171_121' as const, label: '製程1完成日' },
                { field: 'fc171_131' as const, label: '製程2完成日' },
                { field: 'fc171_141' as const, label: '製程3完成日' },
                { field: 'fc171_151' as const, label: '製程4完成日' },
                { field: 'fc171_161' as const, label: '製程5完成日' },
                { field: 'fc171_171' as const, label: '製程6完成日' },
                { field: 'fc171_181' as const, label: '製程7完成日' },
                { field: 'fc171_191' as const, label: '製程8完成日' },
              ]).map(({ field, label }) => (
                <Col span={3} key={field}>
                  <label>{label}</label>
                  <DatePicker
                    size="small"
                    value={details[selectedDetailIdx]?.[field] ? dayjs(details[selectedDetailIdx][field] as string) : null}
                    onChange={d => handleDetailChange(selectedDetailIdx, field, d ? d.toISOString() : null)}
                    disabled={!isEditing}
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                  />
                </Col>
              ))}
            </Row>
          </Card>
        )}
      </Spin>

      {/* Action Bar */}
      <ActionBar
        mode={mode}
        isEditing={isEditing}
        hasPrev={hasPrev}
        hasNext={hasNext}
        saving={saving}
        hasRecord={!!record}
        canCreate={permissions.canCreate}
        canEdit={permissions.canEdit}
        canDelete={permissions.canDelete}
        onFirst={handleFirst}
        onPrev={handlePrev}
        onNext={handleNext}
        onLast={handleLast}
        onSearch={handleSearch}
        onAdd={handleAdd}
        onSave={handleSave}
        onEdit={handleEdit}
        onCancel={handleCancel}
        onDelete={handleDelete}
        onExit={handleExit}
      />
    </div>
  );
};

export default D214Form;
